import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ExternalLink, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DetailTabPanelHeading } from "@/components/common";
import { buttonVariants } from "@/components/ui";
import { getActiveAccessToken } from "@/lib/auth";
import { buildKioskActivityPreviewUrl } from "@/lib/kioskWebUrl";

interface ActivityPreviewSectionProps {
  activityId: string;
}

// Logical resolution of the in-venue kiosk tablet (portrait). The iframe is
// rendered at this exact size so the kiosk's responsive layout behaves like the
// real device, then scaled down to fit the available width in the portal.
const TABLET_WIDTH_PX = 800;
const TABLET_HEIGHT_PX = 1280;

export function ActivityPreviewSection({
  activityId,
}: ActivityPreviewSectionProps) {
  const { t } = useTranslation("activities");
  const previewUrl = buildKioskActivityPreviewUrl(
    activityId,
    getActiveAccessToken(),
  );

  const frameRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    return () => {
      const iframe = iframeRef.current;
      if (iframe) {
        iframe.src = "about:blank";
      }
    };
  }, []);

  useLayoutEffect(() => {
    const element = frameRef.current;
    if (!element) {
      return;
    }

    const updateScale = () => {
      setScale(element.clientWidth / TABLET_WIDTH_PX);
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
      <div className="space-y-4 lg:sticky lg:top-24 lg:flex-1">
        <DetailTabPanelHeading
          title={t("detail.tabs.previewHeading")}
          hint={t("detail.tabs.previewHint")}
        />

        <div className="border-surface-200 bg-surface-50 flex items-start gap-3 rounded-lg border px-4 py-3">
          <Info
            className="text-surface-400 mt-0.5 h-4 w-4 shrink-0"
            aria-hidden
          />
          <p className="text-surface-600 text-[13px]">
            {t("detail.preview.disclaimer")}
          </p>
        </div>

        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          {t("detail.preview.openInNewTab")}
          <ExternalLink
            className="h-4 w-4"
            aria-hidden
          />
        </a>
      </div>

      <div className="w-full max-w-2xl lg:shrink-0 lg:basis-1/2">
        <div className="shadow-elevated rounded-[2.25rem] border border-neutral-700 bg-neutral-900 p-3 ring-1 ring-black/10">
          <div
            ref={frameRef}
            className="w-full"
          >
            {scale > 0 ? (
              <div
                className="bg-surface-100 isolate mx-auto overflow-hidden rounded-[1.5rem]"
                style={{
                  width: TABLET_WIDTH_PX * scale,
                  height: TABLET_HEIGHT_PX * scale,
                }}
              >
                <div
                  className="origin-top-left"
                  style={{
                    width: TABLET_WIDTH_PX,
                    height: TABLET_HEIGHT_PX,
                    transform: `scale(${scale})`,
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    title={t("detail.preview.title")}
                    src={previewUrl}
                    className="size-full border-0"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-surface-100 aspect-[10/16] w-full overflow-hidden rounded-[1.5rem]" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
